import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Card,
  Button,
  Input,
  Select,
  DatePicker,
  Space,
  Tag,
  Modal,
  Form,
  message,
  Image,
  Descriptions,
  Row,
  Col,
} from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  CheckOutlined,
  CloseOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { fetchUsers, updateUser, setFilters, setPagination, clearError } from '../../features/user/userSlice';
import { formatDateTime, maskIdCard, getStatusColor, getStatusText } from '../../utils/index';

const { RangePicker } = DatePicker;
const { Option } = Select;

const UserVerification = () => {
  const dispatch = useDispatch();
  const { users, pagination, filters, isLoading, error } = useSelector((state) => state.user);
  
  const [form] = Form.useForm();
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [approveVisible, setApproveVisible] = useState(false);
  const [rejectVisible, setRejectVisible] = useState(false);

  // 使用JSON.stringify来避免对象引用变化导致的无限循环
  const filtersString = JSON.stringify(filters);
  const paginationString = JSON.stringify(pagination);

  useEffect(() => {
    dispatch(fetchUsers({ 
      ...filters, 
      ...pagination,
      verificationStatus: 'pending' // 默认只显示待审核的
    }));
  }, [dispatch, filtersString, paginationString]);

  useEffect(() => {
    if (error) {
      message.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // 搜索处理
  const handleSearch = (values) => {
    const newFilters = {
      ...values,
      startDate: values.dateRange?.[0]?.format('YYYY-MM-DD'),
      endDate: values.dateRange?.[1]?.format('YYYY-MM-DD'),
    };
    delete newFilters.dateRange;
    dispatch(setFilters(newFilters));
    dispatch(setPagination({ current: 1 }));
  };

  // 重置搜索
  const handleReset = () => {
    form.resetFields();
    dispatch(setFilters({}));
    dispatch(setPagination({ current: 1 }));
  };

  // 表格列配置
  const columns = [
    {
      title: '用户ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '身份证号',
      dataIndex: 'idCard',
      key: 'idCard',
      render: (idCard) => maskIdCard(idCard),
    },
    {
      title: '认证状态',
      dataIndex: 'verificationStatus',
      key: 'verificationStatus',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: '提交时间',
      dataIndex: 'verificationSubmittedAt',
      key: 'verificationSubmittedAt',
      render: (date) => date ? formatDateTime(date) : '-',
    },
    {
      title: '审核时间',
      dataIndex: 'verificationReviewedAt',
      key: 'verificationReviewedAt',
      render: (date) => date ? formatDateTime(date) : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          >
            查看
          </Button>
          {record.verificationStatus === 'pending' && (
            <>
              <Button
                type="text"
                icon={<CheckOutlined />}
                onClick={() => handleApprove(record)}
                style={{ color: '#52c41a' }}
              >
                通过
              </Button>
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={() => handleReject(record)}
                danger
              >
                拒绝
              </Button>
            </>
          )}
        </Space>
      ),
    },
  ];

  // 查看详情
  const handleViewDetail = (user) => {
    setSelectedUser(user);
    setDetailVisible(true);
  };

  // 通过审核
  const handleApprove = (user) => {
    setSelectedUser(user);
    setApproveVisible(true);
  };

  // 拒绝审核
  const handleReject = (user) => {
    setSelectedUser(user);
    setRejectVisible(true);
  };

  // 确认通过
  const handleConfirmApprove = async () => {
    try {
      await dispatch(updateUser({
        userId: selectedUser.id,
        data: {
          verificationStatus: 'approved',
          verificationReviewedAt: new Date().toISOString(),
        },
      })).unwrap();
      message.success('审核通过');
      setApproveVisible(false);
      dispatch(fetchUsers({ ...filters, ...pagination, verificationStatus: 'pending' }));
    } catch (error) {
      message.error(error);
    }
  };

  // 确认拒绝
  const handleConfirmReject = async (values) => {
    try {
      await dispatch(updateUser({
        userId: selectedUser.id,
        data: {
          verificationStatus: 'rejected',
          verificationRejectReason: values.reason,
          verificationReviewedAt: new Date().toISOString(),
        },
      })).unwrap();
      message.success('审核拒绝');
      setRejectVisible(false);
      dispatch(fetchUsers({ ...filters, ...pagination, verificationStatus: 'pending' }));
    } catch (error) {
      message.error(error);
    }
  };

  // 分页处理
  const handleTableChange = (pagination) => {
    dispatch(setPagination({
      current: pagination.current,
      pageSize: pagination.pageSize,
    }));
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">实名认证管理</h1>
        <p className="page-description">审核用户实名认证申请</p>
      </div>

      <Card>
        {/* 搜索表单 */}
        <Form
          form={form}
          layout="inline"
          onFinish={handleSearch}
          style={{ marginBottom: 16 }}
        >
          <Form.Item name="keyword">
            <Input
              placeholder="搜索手机号/姓名/身份证号"
              prefix={<SearchOutlined />}
              style={{ width: 200 }}
            />
          </Form.Item>
          <Form.Item name="verificationStatus">
            <Select placeholder="认证状态" style={{ width: 120 }} allowClear>
              <Option value="pending">待审核</Option>
              <Option value="approved">已通过</Option>
              <Option value="rejected">已拒绝</Option>
            </Select>
          </Form.Item>
          <Form.Item name="dateRange">
            <RangePicker placeholder={['开始日期', '结束日期']} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                搜索
              </Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>

        {/* 认证表格 */}
        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
          }}
          onChange={handleTableChange}
        />
      </Card>

      {/* 认证详情模态框 */}
      <Modal
        title="实名认证详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={800}
      >
        {selectedUser && (
          <div>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={12}>
                <Card title="身份证正面" size="small">
                  <Image
                    src={selectedUser.idCardFrontImage}
                    alt="身份证正面"
                    style={{ width: '100%', maxHeight: 200 }}
                    fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
                  />
                </Card>
              </Col>
              <Col span={12}>
                <Card title="身份证反面" size="small">
                  <Image
                    src={selectedUser.idCardBackImage}
                    alt="身份证反面"
                    style={{ width: '100%', maxHeight: 200 }}
                    fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
                  />
                </Card>
              </Col>
            </Row>

            <Descriptions
              title="认证信息"
              bordered
              column={2}
              size="small"
            >
              <Descriptions.Item label="用户ID">
                {selectedUser.id}
              </Descriptions.Item>
              <Descriptions.Item label="手机号">
                {selectedUser.phone}
              </Descriptions.Item>
              <Descriptions.Item label="姓名">
                {selectedUser.name}
              </Descriptions.Item>
              <Descriptions.Item label="身份证号">
                {selectedUser.idCard}
              </Descriptions.Item>
              <Descriptions.Item label="认证状态">
                <Tag color={getStatusColor(selectedUser.verificationStatus)}>
                  {getStatusText(selectedUser.verificationStatus)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="提交时间">
                {selectedUser.verificationSubmittedAt ? 
                  formatDateTime(selectedUser.verificationSubmittedAt) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="审核时间">
                {selectedUser.verificationReviewedAt ? 
                  formatDateTime(selectedUser.verificationReviewedAt) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="拒绝原因">
                {selectedUser.verificationRejectReason || '-'}
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Modal>

      {/* 通过审核确认模态框 */}
      <Modal
        title="确认通过"
        open={approveVisible}
        onOk={handleConfirmApprove}
        onCancel={() => setApproveVisible(false)}
        okText="确认通过"
        cancelText="取消"
      >
        <p>确定要通过用户 <strong>{selectedUser?.name}</strong> 的实名认证吗？</p>
      </Modal>

      {/* 拒绝审核模态框 */}
      <Modal
        title="拒绝审核"
        open={rejectVisible}
        onCancel={() => setRejectVisible(false)}
        footer={null}
      >
        <Form
          layout="vertical"
          onFinish={handleConfirmReject}
        >
          <Form.Item
            label="拒绝原因"
            name="reason"
            rules={[{ required: true, message: '请输入拒绝原因' }]}
          >
            <Input.TextArea
              placeholder="请输入拒绝原因"
              rows={4}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" danger>
                确认拒绝
              </Button>
              <Button onClick={() => setRejectVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserVerification;
